/**
 * fetchWrapper
 *
 * A thin wrapper around the native fetch API that handles:
 *   - Auth header injection (Bearer token for requests to REACT_APP_API_URL)
 *   - Security headers (X-Frame-Options, Api-key)
 *   - Automatic logout on 401 / 403 responses
 *   - Centralised error logging for all network and API failures
 *
 * Usage:
 *   import { fetchWrapper } from 'helpers/fetch-wrapper';
 *   const data = await fetchWrapper.get('/some/endpoint');
 *   const data = await fetchWrapper.post('/some/endpoint', { key: 'value' });
 */

import { store, authActions } from "./../store/index";
import { activityLogActions } from "./../store/activityLog.slice";
import HttpStatus from "./../helpers/status.enum";
import { logError, classifyApiError, ErrorType, Severity } from "./../monitoring/errorMonitor";

// Not imported from activity-logger.js on purpose: that module also reads/writes
// this same activityLog.sessionId state, and activityLog.slice.js's thunk already
// imports fetchWrapper — importing activity-logger.js here too would add a second
// module into that require cycle. Reading/writing the redux state directly keeps
// the existing cycle shape unchanged while still sharing the same session id.
function getOrCreateSessionId() {
  const currentSessionId = store.getState().activityLog?.sessionId;
  if (currentSessionId) return currentSessionId;
  const sessionId = crypto.randomUUID();
  store.dispatch(activityLogActions.setSessionId(sessionId));
  return sessionId;
}

export const fetchWrapper = {
  get:    request("GET"),
  post:   request("POST"),
  put:    request("PUT"),
  delete: request("DELETE"),
};

function request(method) {
  return (url, body) => {
    const startTime = Date.now();
    const requestOptions = {
      method,
      headers: authHeader(url),
    };
    requestOptions.headers["X-Frame-Options"] = "SAMEORIGIN"; // prevent clickjacking
    requestOptions.headers["Api-key"] = process.env.REACT_APP_API_SECURITY_TOKEN;
    requestOptions.headers["X-Session-Id"] = getOrCreateSessionId();
    if (body) {
      requestOptions.headers["Content-Type"] = "application/json";
      requestOptions.body = JSON.stringify(body);
    }

    // requestInfo is carried forward so handleResponse can include
    // method, url, and timing in the error log without re-capturing them.
    const requestInfo = { url, method, body: body || null, startTime };

    // The .catch() sits directly on fetch(), not after .then(handleResponse).
    // This ensures only true network failures are logged here (offline, DNS,
    // CORS, AbortError). Errors thrown inside handleResponse are separate and
    // must not be classified as network failures.
    return fetch(url, requestOptions)
      .catch((err) => {
        const isTimeout = err?.name === "AbortError" || /timeout|timed out/i.test(err?.message || "");
        logError({
          message:  `Network failure: ${requestInfo.method || "?"} ${requestInfo.url || "?"}`,
          type:     isTimeout ? ErrorType.TIMEOUT_ERROR : ErrorType.NETWORK_ERROR,
          severity: Severity.ERROR,
          apiInfo: {
            url:           requestInfo.url || null,
            method:        requestInfo.method || null,
            status:        0,
            status_text:   err?.name || null,
            request_body:  requestInfo.body || null,
            response_body: null,
            error_message: err?.message || String(err)
          },
        });
        // Re-throw so callers that depend on the rejection keep their existing behaviour.
        throw err;
      })
      .then((response) => handleResponse(response, requestInfo));
  };
}

// Adds an Authorization header only for requests going to our own API.
// Third-party requests (CDN, external services) must not receive the token.
function authHeader(url) {
  const token = authToken();
  if (token && url.startsWith(process.env.REACT_APP_API_URL)) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function authToken() {
  return store.getState().auth.value?.token;
}

async function handleResponse(response, requestInfo = {}) {
  const isJson = response.headers?.get("content-type")?.includes("application/json");
  const data   = isJson ? await response.json() : null;

  if (!response.ok) {
    // Auto-logout on auth failures so the user is sent back to the login page.
    // The check for authToken() prevents a logout dispatch on public endpoints
    // that return 401/403 for unauthenticated users (expected behaviour).
    if (
      [HttpStatus.HTTP_UNAUTHORIZED, HttpStatus.HTTP_FORBIDDEN].includes(response.status) &&
      authToken()
    ) {
      store.dispatch(authActions.logout());
    }

    const classification = classifyApiError(response.status);

    // Log the failed API response through the central error monitor.
    // errorMonitor sanitizes request_body and response_body before sending,
    // so raw objects can be passed here safely.
    logError({
      message:  `API ${response.status}: ${requestInfo.method || "?"} ${requestInfo.url || "?"}`,
      type:     classification.type,
      severity: classification.severity,
      apiInfo: {
        url:           requestInfo.url || null,
        method:        requestInfo.method || null,
        status:        response.status,
        status_text:   response.statusText || null,
        request_body:  requestInfo.body || null,
        response_body: data || null,
        error_message: data?.message || data?.error || null,
      },
    });

    // Return the parsed error body (or bare status code as fallback) so
    // callers can inspect the response even on failure.
    return data || response.status;
  }

  return data;
}
